<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { VDialog } from 'vuetify/components/VDialog';

type Props = {
  modelValue: boolean;
  noDivider?: boolean;
  header?: boolean;
  footer?: boolean;
  fullHeight?: boolean;
  borderGradient?: boolean;
};

type Emits = {
  close: [void];
};

const props = withDefaults(defineProps<Props>(), {
  noDivider: false,
  header: true,
  footer: true,
  fullHeight: false,
  borderGradient: false,
});

const model = defineModel<boolean>({ required: true });

const emits = defineEmits<Emits>();

function close() {
  emits('close');
  model.value = false;
}

const { smAndDown } = useDisplay();

const cardStyle = computed(() => {
  return smAndDown.value
    ? {
        height: props.fullHeight ? '100%' : 'auto',
      }
    : {
        height: 'auto',
      };
});

const contentStyle = computed(() => {
  return smAndDown.value
    ? {
        height: props.fullHeight ? '100%' : undefined,
      }
    : {
        height: 'auto',
      };
});
</script>

<template>
  <VDialog
    v-model="model"
    :content-class="smAndDown ? 'responsive' : ''"
    :transition="smAndDown ? 'slide-y-reverse-transition' : undefined"
    eager
    @click:outside="emits('close')"
  >
    <v-card
      class="elevation-8 max-w-full"
      :min-height="smAndDown ? '30vh' : undefined"
      :max-height="smAndDown ? '100vh' : undefined"
      :class="{
        'rounded-lg': smAndDown && !props.fullHeight,
        'rounded-0': smAndDown && props.fullHeight,
        'border-gradient': props.borderGradient,
      }"
      :style="cardStyle"
    >
      <v-card-title v-if="props.header" class="bg-transparent p-0">
        <div class="text-subtitle-2 p-5 flex justify-between items-center">
          <span>
            <slot name="title"> </slot>
          </span>
          <v-btn icon="fat fa-xmark" variant="plain" size="medium" @click="close"></v-btn>
        </div>
        <v-divider v-if="!props.noDivider" class="w-full"></v-divider>
      </v-card-title>

      <v-card-text
        class="relative overflow-y-auto"
        :class="[!props.noDivider ? 'p-5' : 'px-5 py-0']"
        :style="contentStyle"
      >
        <v-btn
          v-if="!props.header"
          icon="fat fa-xmark"
          variant="plain"
          @click="close"
          class="absolute top-2 end-2"
        ></v-btn>

        <slot name="content"></slot>
      </v-card-text>

      <v-card-actions v-if="props.footer" class="p-0 flex-col">
        <v-divider class="w-full"></v-divider>

        <div class="text-subtitle-2 p-5 flex gap-5 justify-end items-center w-full">
          <slot name="footer"></slot>
        </div>
      </v-card-actions>
    </v-card>
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.responsive > .v-card) {
  position: absolute;
  bottom: 0;
  margin: 0 !important;
}

.border-gradient {
  background:
    linear-gradient(#ffffff, #ffffff) 50% 50% / calc(100% - 10px) calc(100% - 10px) no-repeat,
    linear-gradient(90deg, rgba(255, 47, 146, 0.6) 0%, rgba(245, 159, 0, 0.62) 100%);
  background-color: #ffffff;
  border-radius: 4px;
  padding: 9px;
  box-sizing: border-box;
}
</style>
